<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 mb-2">Gestion des candidats</h1>
            <p class="text-subtitle-1 text-grey">
              Consultez et gérez les candidatures reçues
            </p>
          </div>
          <div class="d-flex gap-2">
            <v-btn color="secondary" prepend-icon="mdi-download" @click="exportCandidates" :loading="exporting">
              Exporter
            </v-btn>
            <v-btn color="primary" prepend-icon="mdi-email-multiple" @click="bulkEmailDialog = true"
              :disabled="selectedCandidates.length === 0">
              Email groupé
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters and Search -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-text-field v-model="searchTerm" prepend-inner-icon="mdi-magnify" label="Rechercher..." variant="outlined"
          density="compact" clearable></v-text-field>
      </v-col>
      <v-col cols="12" md="2">
        <v-select v-model="selectedStatus" :items="statusOptions" label="Statut" variant="outlined" density="compact"
          clearable></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-select v-model="selectedJob" :items="jobOptions" label="Poste" variant="outlined" density="compact"
          clearable></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-select v-model="selectedExperience" :items="experienceOptions" label="Expérience" variant="outlined"
          density="compact" clearable></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-btn variant="outlined" block @click="resetFilters">
          Réinitialiser
        </v-btn>
      </v-col>
      <v-col cols="12" md="1">
        <v-btn icon="mdi-filter" @click="advancedFiltersDialog = true"></v-btn>
      </v-col>
    </v-row>

    <!-- Candidates List -->
    <v-card>
      <v-data-table v-model="selectedCandidates" :headers="headers" :items="filteredCandidates" :loading="loading"
        class="elevation-1" item-key="id" show-select return-object>
        <template v-slot:item.candidate="{ item }">
          <div class="d-flex align-center">
            <v-avatar class="me-3">
              <v-img v-if="item.avatar" :src="item.avatar" :alt="item.fullName"></v-img>
              <v-icon v-else>mdi-account</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.full_name }}</div>
              <div class="text-caption text-grey">{{ item.email }}</div>
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" size="small">
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.score="{ item }">
          <div class="d-flex align-center">
            <v-progress-circular :model-value="calculateScore(item)" :color="getScoreColor(calculateScore(item))"
              size="30" width="3">
              <span class="text-caption">{{ calculateScore(item) }}</span>
            </v-progress-circular>
          </div>
        </template>

        <template v-slot:item.appliedAt="{ item }">
          {{ formatDate(item.appliedAt) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon="mdi-eye" size="small" variant="text" @click="viewCandidate(item)"></v-btn>
          <v-btn icon="mdi-account-file-outline" size="small" variant="text" color="red" @click="viewCV(item)"></v-btn>
          <v-btn icon="mdi-calendar" size="small" variant="text" color="info" @click="scheduleInterview(item)"></v-btn>
          <v-btn icon="mdi-email" size="small" variant="text" color="success" @click="sendEmail(item)"></v-btn>
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn icon="mdi-dots-vertical" size="small" variant="text" v-bind="props"></v-btn>
            </template>
            <v-list>
              <v-list-item @click="changeStatus(item, 'ACCEPTED')">
                <v-list-item-title>Accepter</v-list-item-title>
              </v-list-item>
              <v-list-item @click="changeStatus(item, 'UNDER_REVIEW')">
                <v-list-item-title>En examen</v-list-item-title>
              </v-list-item>
              <v-list-item @click="changeStatus(item, 'SHORTLISTED')">
                <v-list-item-title>liste restreinte</v-list-item-title>
              </v-list-item>
              <v-list-item @click="changeStatus(item, 'REJECTED')">
                <v-list-item-title>Rejeter</v-list-item-title>
              </v-list-item>
              <v-list-item @click="changeStatus(item, 'SELECTED')">
                <v-list-item-title>Entretien</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialog CV PDF -->
    <v-dialog v-model="cvDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>CV du candidat</span>
          <v-btn icon="mdi-close" variant="text" @click="cvDialog = false"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="height: 70vh;">
          <iframe v-if="cvPdfUrl" :src="cvPdfUrl" width="100%" height="100%" style="border: none;"></iframe>
          <div v-else class="text-center py-8">Chargement du CV...</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!cvPdfUrl" @click="downloadCV">Télécharger</v-btn>
          <v-btn variant="text" @click="cvDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Email Dialog -->
    <v-dialog v-model="bulkEmailDialog" max-width="600px">
      <v-card>
        <v-card-title>Email groupé</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ selectedCandidates.length }} candidat(s) sélectionné(s)</p>
          <v-select v-model="emailTemplate" :items="emailTemplates" label="Modèle d'email"
            variant="outlined"></v-select>
          <v-textarea v-model="emailContent" label="Contenu de l'email" variant="outlined" rows="6"
            class="mt-4"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="bulkEmailDialog = false">
            Annuler
          </v-btn>
          <v-btn color="primary" @click="sendBulkEmail">
            Envoyer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Advanced Filters Dialog -->
    <v-dialog v-model="advancedFiltersDialog" max-width="500px">
      <v-card>
        <v-card-title>Filtres avancés</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select v-model="advancedFilters.skills" :items="skillOptions" label="Compétences" variant="outlined"
                multiple chips></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="advancedFilters.minScore" label="Score minimum" type="number" min="0" max="100"
                variant="outlined"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="advancedFilters.maxScore" label="Score maximum" type="number" min="0" max="100"
                variant="outlined"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="resetAdvancedFilters">
            Réinitialiser
          </v-btn>
          <v-btn color="primary" @click="applyAdvancedFilters">
            Appliquer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Détail Candidat -->
    <v-dialog v-model="candidateDialog" max-width="1000px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Détail du candidat</span>
          <v-btn icon="mdi-close" variant="text" @click="candidateDialog = false"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-tabs v-model="candidateTab" grow>
            <v-tab>Informations</v-tab>
            <v-tab>Présentation & Questionnaire</v-tab>
          </v-tabs>
          <v-window v-model="candidateTab">
            <v-window-item :value="0">
              <div v-if="selectedCandidate">
                <v-row>
                  <v-col cols="12" md="6">
                    <strong>Nom :</strong> {{ selectedCandidate.nom }}<br>
                    <strong>Prénom :</strong> {{ selectedCandidate.prenom }}<br>
                    <strong>Email :</strong> {{ selectedCandidate.email }}<br>
                    <strong>Poste :</strong> {{ selectedCandidate.fichePosteTitle }}<br>
                    <strong>Date de candidature :</strong> {{ formatDate(selectedCandidate.appliedAt) }}<br>
                    <strong>Statut :</strong> {{ getStatusText(selectedCandidate.status) }}<br>
                  </v-col>
                  <v-col cols="12" md="6">
                    <strong>Score global :</strong>
                    <v-progress-circular v-if="selectedCandidate.candidatEval?.overall?.score !== undefined"
                      :model-value="selectedCandidate.candidatEval.overall.score * 100"
                      :color="getScoreColor(selectedCandidate.candidatEval.overall.score * 100)" size="40" width="4">
                      <span class="text-caption">{{ Math.round(selectedCandidate.candidatEval.overall.score * 100)
                      }}</span>
                    </v-progress-circular>
                    <span v-else>N/A</span>
                    <div v-if="selectedCandidate.candidatEval?.overall">
                      <strong>Niveau de correspondance :</strong> {{ selectedCandidate.candidatEval.overall.match_level
                      }}<br>
                      <strong>Résumé :</strong> {{ selectedCandidate.candidatEval.overall.summary }}<br>
                      <strong>Explication :</strong> {{ selectedCandidate.candidatEval.overall.explanation }}<br>
                    </div>
                  </v-col>
                </v-row>
                <v-divider class="my-4"></v-divider>
                <div v-if="selectedCandidate.candidatEval">
                  <h4 class="text-h6 mb-2">Détail de l'évaluation</h4>
                  <v-row>
                    <v-col cols="12" md="6">
                      <strong>Compétences :</strong> {{ selectedCandidate.candidatEval.skillsMatch?.details }}<br>
                      <strong>Score :</strong> {{ Math.round((selectedCandidate.candidatEval.skillsMatch?.score || 0) *
                        100)
                      }}
                    </v-col>
                    <v-col cols="12" md="6">
                      <strong>Expérience :</strong> {{ selectedCandidate.candidatEval.experienceMatch?.details }}<br>
                      <strong>Score :</strong> {{ Math.round((selectedCandidate.candidatEval.experienceMatch?.score ||
                        0) *
                        100) }}
                    </v-col>
                    <v-col cols="12" md="6">
                      <strong>Formation :</strong> {{ selectedCandidate.candidatEval.educationMatch?.details }}<br>
                      <strong>Score :</strong> {{ Math.round((selectedCandidate.candidatEval.educationMatch?.score || 0)
                        *
                        100) }}
                    </v-col>
                    <v-col cols="12" md="6">
                      <strong>Langues/Certifications :</strong> {{
                        selectedCandidate.candidatEval.languagesCertificationsMatch?.details }}<br>
                      <strong>Score :</strong> {{
                        Math.round((selectedCandidate.candidatEval.languagesCertificationsMatch?.score || 0) * 100) }}
                    </v-col>
                  </v-row>
                </div>
              </div>
              <div v-else>Chargement...</div>
            </v-window-item>
            <v-window-item :value="1">
              <div v-if="selectedCandidate">
                <h4 class="text-h6 mb-2">Présentation vidéo</h4>
                <div v-if="videoUrl">
                  <video controls style="max-width:100%;height:auto"
                    :aria-label="'Présentation vidéo de ' + (selectedCandidate.nom || selectedCandidate.full_name)"
                    aria-describedby="video-desc">
                    <source :src="videoUrl" type="video/webm" />
                    <!-- Sous-titres placeholder, à remplacer par un vrai fichier si disponible -->
                    <track kind="subtitles" srclang="fr" label="Français" v-if="selectedCandidate.presentationSubtitles"
                      :src="selectedCandidate.presentationSubtitles" default />
                    <track kind="subtitles" srclang="fr" label="Aucun sous-titre disponible" v-else>
                    Votre navigateur ne supporte pas la vidéo.
                  </video>
                  <div id="video-desc" class="visually-hidden">
                    Vidéo de présentation du candidat. Contactez le candidat pour obtenir une transcription si besoin.
                  </div>
                </div>
                <div v-else class="text-grey">Aucune vidéo fournie.</div>
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between align-center">
                  <h4 class="text-h6 mb-2">Résultat du questionnaire</h4>
                  <v-btn
                    v-if="selectedCandidate.questionnaireInfo && (parsedQuestionnaire && parsedQuestionnaire.length)"
                    :icon="questionnaireExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" variant="text" size="small"
                    color="primary" @click="questionnaireExpanded = !questionnaireExpanded">
                    <v-tooltip activator="parent" location="top">
                      {{ questionnaireExpanded ? 'Masquer les détails' : 'Voir les questions/réponses' }}
                    </v-tooltip>
                  </v-btn>
                </div>
                <div v-if="selectedCandidate.questionnaireInfo">
                  <v-row class="mb-4">
                    <v-col cols="12">
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                          <strong class="mr-2">Score questionnaire :</strong>
                          <v-progress-circular :model-value="getQuestionnaireScore(selectedCandidate.questionnaireInfo)"
                            :color="getScoreColor(getQuestionnaireScore(selectedCandidate.questionnaireInfo))" size="40"
                            width="4" class="ml-2">
                            <span class="text-caption">{{ getQuestionnaireScore(selectedCandidate.questionnaireInfo)
                              }}%</span>
                          </v-progress-circular>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon v-bind="props" icon="mdi-information-outline" size="small" color="grey"
                                class="ml-2 cursor-pointer">
                              </v-icon>
                            </template>
                            <div class="text-body-2">
                              <strong>Détails du questionnaire :</strong><br>
                              {{ selectedCandidate.questionnaireInfo.nombreReponsesCorrectes }}/{{
                                selectedCandidate.questionnaireInfo.nombreQuestions }}
                              bonnes réponses<br>
                              <span v-if="selectedCandidate.questionnaireInfo.duree">
                                Durée : {{ selectedCandidate.questionnaireInfo.duree }}<br>
                              </span>
                              <span v-if="selectedCandidate.questionnaireInfo.note">
                                Note : {{ selectedCandidate.questionnaireInfo.note }}/20
                              </span>
                            </div>
                          </v-tooltip>
                        </div>
                        <div v-if="parsedQuestionnaire && parsedQuestionnaire.length"
                          class="d-flex align-center text-body-2 text-primary cursor-pointer"
                          @click="questionnaireExpanded = !questionnaireExpanded">
                          <v-icon :icon="questionnaireExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="small"
                            class="mr-1"></v-icon>
                          {{ questionnaireExpanded ? 'Masquer' : 'Voir' }} les questions/réponses
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <v-expand-transition>
                    <div v-if="questionnaireExpanded && parsedQuestionnaire && parsedQuestionnaire.length">
                      <v-divider class="my-4"></v-divider>
                      <h5 class="text-subtitle-1 mb-3">Questions et réponses</h5>
                      <v-list density="compact">
                        <v-list-item v-for="(q, idx) in parsedQuestionnaire" :key="idx">
                          <v-list-item-content>
                            <div class="font-weight-medium">{{ q.question }}</div>
                            <div class="text-grey">Réponse : <span class="text-body-2">{{ q.candidat_answer }}</span>
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-expand-transition>
                </div>
                <div v-else class="text-grey">Aucun questionnaire renseigné.</div>
              </div>
              <div v-else>Chargement...</div>
            </v-window-item>
          </v-window>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="candidateDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import apiService from '@/services/api'
import { useHRStore } from '@/stores/hr'
import { useSnackbarStore } from '@/stores/snackbar'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const hrStore = useHRStore()
const snackbarStore = useSnackbarStore()

const loading = ref(false)
const exporting = ref(false)
const selectedCandidates = ref([])
const bulkEmailDialog = ref(false)
const advancedFiltersDialog = ref(false)
const cvDialog = ref(false)
const candidateDialog = ref(false)
const selectedCandidate = ref<any>(null)
const candidateTab = ref(0)
const questionnaireExpanded = ref(false)

const searchTerm = ref('')
const selectedStatus = ref('')
const selectedJob = ref('')
const selectedExperience = ref('')

const emailTemplate = ref('')
const emailContent = ref('')

const advancedFilters = ref({
  skills: [],
  minScore: null,
  maxScore: null
})

const candidates = ref([])
const loadingCandidates = ref(false)

const cvPdfUrl = ref(null)
let cvBlob = null

const videoUrl = computed(() => {
  if (!selectedCandidate.value) return null
  // Utilise l'URL dynamique générée par l'API
  return apiService.hr.getCandidatePresentationUrl(selectedCandidate.value.id)
})

const fetchCandidates = async () => {
  loadingCandidates.value = true
  try {
    const res = await apiService.hr.getCandidates()
    candidates.value = res.data.map(c => ({
      ...c,
      name: c.name || `${c.firstName} ${c.lastName}`
    }))
  } catch (e) {
    console.error('Erreur lors du chargement des candidats', e)
    snackbarStore.error('Erreur lors du chargement des candidats')
  } finally {
    loadingCandidates.value = false
  }
}

onMounted(() => {
  fetchCandidates()
})

const viewCV = async (candidate) => {
  try {
    const res = await apiService.hr.downloadCV(candidate.id)
    const blob = new Blob([res.data], { type: 'application/pdf' })
    cvPdfUrl.value = URL.createObjectURL(blob)
    cvDialog.value = true
  } catch (e) {
    console.error("Impossible d'ouvrir le CV", e)
    snackbarStore.error("Impossible d'ouvrir le CV")
  }
}

const downloadCV = () => {
  if (cvBlob && cvPdfUrl.value) {
    const link = document.createElement('a')
    link.href = cvPdfUrl.value
    link.download = 'cv.pdf'
    link.click()
  }
}

const headers = [
  { title: 'Candidat', key: 'candidate', sortable: false },
  { title: 'Poste', key: 'jobTitle', sortable: true },
  { title: 'Statut', key: 'status', sortable: true },
  { title: 'Score', key: 'score', sortable: true },
  { title: 'Expérience', key: 'experience', sortable: true },
  { title: 'Candidature', key: 'appliedAt', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const statusOptions = [
  { title: 'En attente', value: 'SUBMITTED' },
  { title: 'Entretien', value: 'SELECTED' },
  { title: 'Accepté', value: 'ACCEPTED' },
  { title: 'Rejeté', value: 'REJECTED' },
  { title: 'En examen', value: 'UNDER_REVIEW' },
  { title: 'Shortlist', value: 'SHORTLISTED' }
]

const jobOptions = [
  { title: 'Développeur Full Stack', value: 'dev-fullstack' },
  { title: 'Chef de projet', value: 'chef-projet' },
  { title: 'Marketing Manager', value: 'marketing-manager' }
]

const experienceOptions = [
  { title: 'Junior (0-2 ans)', value: 'junior' },
  { title: 'Intermédiaire (2-5 ans)', value: 'intermediate' },
  { title: 'Senior (5+ ans)', value: 'senior' }
]

const skillOptions = [
  'Vue.js', 'React', 'Angular', 'Node.js', 'Python', 'Java',
  'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'Kubernetes',
  'Management', 'Agile', 'Scrum', 'Marketing', 'SEO'
]

const emailTemplates = [
  { title: 'Accusé de réception', value: 'acknowledgment' },
  { title: 'Convocation entretien', value: 'interview-invitation' },
  { title: 'Réponse positive', value: 'acceptance' },
  { title: 'Réponse négative', value: 'rejection' }
]

const filteredCandidates = computed(() => {
  let filtered = candidates.value

  if (searchTerm.value) {
    filtered = filtered.filter(candidate =>
      candidate.name.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      candidate.email.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      candidate.jobTitle.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
  }

  if (selectedStatus.value) {
    filtered = filtered.filter(candidate => candidate.status === selectedStatus.value)
  }

  if (selectedJob.value) {
    filtered = filtered.filter(candidate =>
      candidate.jobTitle.toLowerCase().includes(selectedJob.value.toLowerCase())
    )
  }

  if (selectedExperience.value) {
    filtered = filtered.filter(candidate => candidate.experience === selectedExperience.value)
  }

  // Apply advanced filters
  if (advancedFilters.value.skills.length > 0) {
    filtered = filtered.filter(candidate =>
      advancedFilters.value.skills.some(skill =>
        candidate.skills.includes(skill)
      )
    )
  }

  if (advancedFilters.value.minScore) {
    filtered = filtered.filter(candidate =>
      candidate.score >= advancedFilters.value.minScore
    )
  }

  if (advancedFilters.value.maxScore) {
    filtered = filtered.filter(candidate =>
      candidate.score <= advancedFilters.value.maxScore
    )
  }

  return filtered
})

const calculateScore = (candidate) => {
  if (!candidate.candidatEval || !candidate.candidatEval.overall) return 0
  let score = candidate.candidatEval.overall.score * 100
  return Math.round(score)
}

const getStatusColor = (status) => {
  const colors = {
    SUBMITTED: 'lime',
    UNDER_REVIEW: 'light-green',
    SHORTLISTED: 'green',
    SELECTED: 'teal',
    ACCEPTED: 'light-blue',
    REJECTED: 'red'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status) => {
  const texts = {
    SUBMITTED: 'soumis',
    UNDER_REVIEW: 'En examen',
    SHORTLISTED: 'liste restreinte',
    SELECTED: 'sélectionné',
    ACCEPTED: 'accepté',
    REJECTED: 'rejeté'
  }
  return texts[status] || status
}

const getScoreColor = (score) => {
  if (score >= 80) return 'success'
  if (score >= 60) return 'warning'
  return 'error'
}

const getQuestionnaireScore = (questionnaireInfo) => {
  if (!questionnaireInfo || !questionnaireInfo.nombreQuestions) return 0
  return Math.round((questionnaireInfo.nombreReponsesCorrectes / questionnaireInfo.nombreQuestions) * 100)
}

const formatDate = (date) => {
  return format(new Date(date), 'dd/MM/yyyy', { locale: fr })
}

const resetFilters = () => {
  searchTerm.value = ''
  selectedStatus.value = ''
  selectedJob.value = ''
  selectedExperience.value = ''
}

const resetAdvancedFilters = () => {
  advancedFilters.value = {
    skills: [],
    minScore: null,
    maxScore: null
  }
}

const applyAdvancedFilters = () => {
  advancedFiltersDialog.value = false
}

const viewCandidate = async (candidate) => {
  try {
    const res = await apiService.hr.getCandidate(candidate.id)
    selectedCandidate.value = res.data
    questionnaireExpanded.value = false // Réinitialise l'état du questionnaire
    candidateDialog.value = true
  } catch (e) {
    console.error("Erreur lors du chargement du candidat:", e)
    snackbarStore.error("Impossible de charger le détail du candidat")
  }
}

const scheduleInterview = (candidate) => {
  // Open interview scheduling dialog
  snackbarStore.info('Fonction de planification d\'entretien à implémenter')
}

const sendEmail = (candidate) => {
  // Open email composer
  snackbarStore.info('Fonction d\'envoi d\'email à implémenter')
}

const changeStatus = async (candidate, newStatus) => {
  try {
    const res = await apiService.hr.updateCandidateStatus(candidate.id, { status: newStatus })
    // Met à jour la ligne modifiée dans le tableau
    const index = candidates.value.findIndex(c => c.id === candidate.id)
    if (index > -1) {
      candidates.value[index] = { ...candidates.value[index], ...res.data }
    }
    snackbarStore.success(`Statut du candidat modifié: ${getStatusText(newStatus)}`)
  } catch (e) {
    console.error('Erreur lors du changement de statut', e)
    snackbarStore.error("Erreur lors du changement de statut")
  }
}

const exportCandidates = async () => {
  exporting.value = true
  try {
    // Simulate export
    await new Promise(resolve => setTimeout(resolve, 2000))
    snackbarStore.success('Export des candidats terminé')
  } catch (error) {
    console.error('Erreur lors de l\'export', error)
    snackbarStore.error('Erreur lors de l\'export')
  } finally {
    exporting.value = false
  }
}

const sendBulkEmail = async () => {
  try {
    // Simulate sending bulk email
    await new Promise(resolve => setTimeout(resolve, 1000))
    snackbarStore.success(`Email envoyé à ${selectedCandidates.value.length} candidat(s)`)
    bulkEmailDialog.value = false
    selectedCandidates.value = []
  } catch (error) {
    console.error('Erreur lors de l\'envoi des emails', error)
    snackbarStore.error('Erreur lors de l\'envoi des emails')
  }
}

const parsedQuestionnaire = computed(() => {
  if (!selectedCandidate.value || !selectedCandidate.value.questionnaireRepondu) return []
  try {
    const data = JSON.parse(selectedCandidate.value.questionnaireRepondu)
    // data peut être un tableau [{question, answer}] ou un objet clé/valeur
    if (Array.isArray(data)) return data
    if (typeof data === 'object') {
      return Object.entries(data).map(([question, answer]) => ({ question, answer }))
    }
    return []
  } catch (e) {
    console.error('Erreur lors du parsing du questionnaire', e)
    return []
  }
})
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
